import React, {useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components/native';
import {Keyboard, View} from 'react-native';
import {Theme} from '../../../theme/lightTheme';
import {tr} from '../../../translations';
import TextInput from '../../../components/textInput';
import GenericButton from '../../../components/button';
import Animated, {FadeIn, FadeOut} from 'react-native-reanimated';
import TextGeneric from '../../../components/textGeneric';
import {ChangePasswordBody} from '../../../interfaces/api/tabs';
import {changePassword} from './actions';
import {getFontFamily} from '../../../utils/helpers';
import {BottomSheetFlatList} from '@gorhom/bottom-sheet';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {isWeb} from '../../../utils/responsive';

const Container = styled.View`
  flex: 1;
  ${isWeb &&
  `
    height: 100%;
    min-height: 500px;
    overflow: scroll;
  `}
`;

const ContentContainer = styled.View`
  flex: 1;
`;

const Separator = styled.View`
  height: ${({theme}) => theme.text.s55}px;
`;
const ErrorText = styled(TextGeneric)`
  color: ${({theme}) => theme.text.error};
  font-size: ${({theme}) => theme.text.s9}px;
  padding-top: ${({theme}) => theme.text.s55}px;
  text-align: center;
`;
const ErrorContainer = styled.View`
  min-height: ${({theme}) => theme.text.s10 * 5}px;
`;
const SubmitButton = styled(GenericButton)<{
  isWeb?: boolean;
  responsiveWidth: number;
}>`
  ${({isWeb, theme, responsiveWidth}) =>
    isWeb
      ? `
    width: 40%;
    align-self: center;
    margin: ${theme.text.s55}px 6%;
    border-radius: 8px;
  `
      : `
    margin-top: ${theme.text.s55}px;
    width: ${responsiveWidth}px;
    align-self: center;
  `}
  background-color: ${({disabled, theme}) =>
    disabled ? theme.primary + 'AA' : theme.primary};
`;

const FooterContainer = styled.View<{isWeb?: boolean}>`
  ${({isWeb, theme}) =>
    !isWeb &&
    `
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding-bottom: ${theme.text.s55}px;
  `}
`;

const WebFormContainer = styled.View`
  padding: 3% 6%;
`;

// Define the mandatory fields for the form
const mandatoryFields = ['oldPassword', 'newPassword', 'confirmPassword'];
/**
 * ChangePassword screen component.
 *
 * @param {any} props - The screen component props.
 */
const ChangePassword: React.FC<any> = () => {
  const theme = useTheme() as Theme;
  const {isMobile, hdp, wdp} = useResponsiveDimensions();

  const [form, updateForm] = useState<any>({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  // Define the form completion state
  const [formComplete, setFormComplete] = useState(false);
  // Define the error state
  const [isError, setIsError] = useState(false);

  // Function to handle changes to the form fields
  const handleChange = (key: string, value: string) => {
    updateForm({
      ...form,
      [key]: value,
    });
  };
  // Effect to check if the form is complete
  useEffect(() => {
    let _formComplete = true;
    for (let index = 0; index < mandatoryFields.length; index++) {
      const field = mandatoryFields[index];
      if (!form[field] || form[field].length <= 0) {
        setFormComplete(false);
        _formComplete = false;
        break;
      }
    }
    if (_formComplete) {
      setFormComplete(true);
    }
  }, [form]);

  // Function to handle form submission
  const submit = async () => {
    setIsError(false);
    Keyboard.dismiss();

    const body: ChangePasswordBody = {
      old_password: form?.oldPassword,
      new_password: form?.newPassword,
      new_password_confirmation: form?.confirmPassword,
    };
    const res = await changePassword(body);
    setIsError(!res);
  };
  const _renderItem = ({item}: {item: any}) => {
    return (
      <TextInput
        label={tr(`changePassword.${item}`)}
        labelStyle={{
          fontFamily: getFontFamily().bold,
        }}
        placeholder={''}
        value={form[item]}
        onChangeText={text => handleChange(item, text)}
        secureInput
        containerStyle={{marginTop: hdp(1)}}
        showLeftIcon
        leftIconName={'lock'}
        leftIconType={'SimpleLineIcons'}
        leftIconColor={theme.secondary}
      />
    );
  };

  const renderWebForm = () => (
    <WebFormContainer>
      {mandatoryFields.map((item, index) => (
        <React.Fragment key={item}>
          {index > 0 && <View style={{height: hdp(1.5)}} />}
          {_renderItem({item})}
        </React.Fragment>
      ))}
      <ErrorContainer>
        {isError && (
          <Animated.View
            style={{width: wdp(99)}}
            entering={FadeIn}
            exiting={FadeOut}>
            <ErrorText>{tr('changePassword.error')}</ErrorText>
          </Animated.View>
        )}
      </ErrorContainer>
      <SubmitButton
        responsiveWidth={wdp(90)}
        isWeb={isWeb && !isMobile}
        label={tr('changePassword.save')}
        onPress={submit}
        disabled={!formComplete || form.newPassword !== form.confirmPassword}
        backgroundColor={
          !formComplete || form.newPassword !== form.confirmPassword
            ? theme.primary + '22'
            : theme.primary
        }
        labelStyle={{
          color:
            !formComplete || form.newPassword !== form.confirmPassword
              ? theme.text.light
              : theme.text.light,
        }}
      />
    </WebFormContainer>
  );

  const renderMobileForm = () => (
    <>
      <BottomSheetFlatList
        data={mandatoryFields}
        renderItem={_renderItem}
        contentContainerStyle={{
          paddingHorizontal: '6%',
          paddingTop: '3%',
        }}
        ItemSeparatorComponent={Separator}
        scrollEnabled={true}
      />
      <FooterContainer isWeb={isWeb}>
        <ErrorContainer>
          {isError && (
            <Animated.View
              style={{width: wdp(99)}}
              entering={FadeIn}
              exiting={FadeOut}>
              <ErrorText>{tr('changePassword.error')}</ErrorText>
            </Animated.View>
          )}
        </ErrorContainer>
        <SubmitButton
          responsiveWidth={wdp(90)}
          isWeb={isWeb && !isMobile}
          label={tr('changePassword.save')}
          onPress={submit}
          disabled={!formComplete || form.newPassword !== form.confirmPassword}
          backgroundColor={
            !formComplete || form.newPassword !== form.confirmPassword
              ? theme.primary + '22'
              : theme.primary
          }
          labelStyle={{
            color:
              !formComplete || form.newPassword !== form.confirmPassword
                ? theme.text.light
                : theme.text.light,
          }}
        />
      </FooterContainer>
    </>
  );

  return (
    <Container>
      <ContentContainer>
        {isWeb ? renderWebForm() : renderMobileForm()}
      </ContentContainer>
    </Container>
  );
};

export default ChangePassword;
